import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { MaskedLines, Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import EnquiryModal from "../components/EnquiryModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProductsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [quoteProduct, setQuoteProduct] = useState(null);

  const active = searchParams.get("category") || "all";

  useEffect(() => {
    let mounted = true;
    Promise.all([
      fetch(`${API}/products`).then((r) => r.json()),
      fetch(`${API}/categories`).then((r) => r.json()),
    ])
      .then(([prods, cats]) => {
        if (!mounted) return;
        setProducts(Array.isArray(prods) ? prods : []);
        setCategories(Array.isArray(cats) ? cats : []);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return products.filter((p) => {
      if (active !== "all" && p.category !== active) return false;
      if (!q) return true;
      return (
        p.name.toLowerCase().includes(q) ||
        p.tagline.toLowerCase().includes(q) ||
        p.hs_code.toLowerCase().includes(q)
      );
    });
  }, [products, active, search]);

  const tabs = [{ slug: "all", name: "All Products" }, ...categories.map((c) => ({ slug: c.slug, name: c.name }))];

  return (
    <div data-testid="page-products" className="bg-mist">
      <section className="relative overflow-hidden pt-[72px]">
        <div className="dot-grid pointer-events-none absolute inset-0" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 pb-12 pt-16 sm:px-6 lg:px-8 lg:pb-16 lg:pt-24">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-emerald-700">
              <span className="h-[1px] w-8 bg-emerald-700/40" />
              Export Catalog
            </p>
          </Reveal>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            <MaskedLines
              delay={0.2}
              lines={[
                "Twelve lines,",
                <span key="s" className="italic text-forest">
                  one standard.
                </span>,
              ]}
            />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Every product below ships with its grade sheet, test report and HS classification.
              Click through for full specifications and packaging.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="catalog-section" className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        {/* Filters */}
        <Reveal>
          <div className="flex flex-col gap-4 rounded-3xl border border-slate-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between md:p-5">
            <div className="flex flex-wrap items-center gap-2" data-testid="category-filter-group">
              <SlidersHorizontal className="mr-1 hidden h-4 w-4 text-slate-400 md:block" />
              {tabs.map((tab) => (
                <button
                  key={tab.slug}
                  data-testid={`category-filter-${tab.slug}`}
                  onClick={() => setSearchParams(tab.slug === "all" ? {} : { category: tab.slug })}
                  className={`rounded-full px-4 py-2 font-mono text-[11px] font-semibold uppercase tracking-[0.14em] transition-all ${
                    active === tab.slug
                      ? "bg-forest text-white shadow-[0_8px_20px_-8px_rgba(6,78,59,0.6)]"
                      : "border border-slate-200 bg-white text-slate-500 hover:border-forest/40 hover:text-forest"
                  }`}
                >
                  {tab.name}
                </button>
              ))}
            </div>
            <div className="relative md:w-72">
              <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                data-testid="catalog-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search product, grade, HS code…"
                className="w-full rounded-full border border-slate-200 bg-mist py-2.5 pl-11 pr-4 text-sm text-ink outline-none transition-colors placeholder:text-slate-400 focus:border-forest/50"
              />
            </div>
          </div>
        </Reveal>

        {/* Grid */}
        <div className="mt-10" data-testid="product-grid">
          {loading ? (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-96 animate-pulse rounded-3xl bg-slate-200/70" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-20 text-center" data-testid="catalog-empty-state">
              <p className="font-display text-xl font-semibold text-ink">No products match</p>
              <p className="mt-2 text-sm text-slate-500">
                Try a different keyword or clear the filters.
              </p>
              <button
                data-testid="catalog-reset-button"
                onClick={() => {
                  setSearch("");
                  setSearchParams({});
                }}
                className="mt-6 rounded-full bg-forest px-6 py-2.5 text-sm font-semibold text-white hover:bg-forest-hover"
              >
                Reset Filters
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} onRequestQuote={setQuoteProduct} />
              ))}
            </div>
          )}
        </div>
      </section>

      <EnquiryModal
        open={!!quoteProduct}
        onClose={() => setQuoteProduct(null)}
        product={quoteProduct}
        products={products}
      />
    </div>
  );
}
